import type { Device, SpeedSetting } from '../lib/types';

const SLOTS: Array<keyof SpeedSetting> = ['S1', 'S2', 'S3', 'S4'];

/**
 * Speed-preset chips. iOS-parity layout (mirrors
 * `Mobile/iOS/AquaStar/Views/Controls/SpeedButton.swift`):
 *
 *   ┌────┐    ┌────┐    ┌────┐    ┌────┐
 *   │ S1 │    │ S2 │    │ S3 │    │ S4 │
 *   └────┘    └────┘    └────┘    └────┘
 *    1,111     1,610     2,691    3,194 rpm
 *
 * Circular chip per slot, RPM value beneath. Active slot = solid brand
 * blue; non-active selectable = brand-blue 30%; disabled = grey.
 *
 * When `onSelect` is omitted (or `disabled`), the chips render as a
 * read-only display.
 */
export function PresetButtons({
  device,
  disabled = false,
  busySlot = null,
  onSelect,
}: {
  device: Device;
  disabled?: boolean;
  busySlot?: keyof SpeedSetting | null;
  onSelect?: (slot: keyof SpeedSetting) => void;
}) {
  const selected = device.selectedSpeed;
  const isInteractive = !disabled && !!onSelect;

  return (
    <div
      className="d-flex justify-content-around align-items-start flex-wrap gap-2"
      role={isInteractive ? 'group' : undefined}
      aria-label="Speed presets"
    >
      {SLOTS.map((slot) => {
        const rpm = device.speedSetting?.[slot] ?? 0;
        const isActive = selected === slot;
        const isBusy = busySlot === slot;

        const chipBg = disabled
          ? '#c8cdd6'
          : isActive
            ? 'var(--aqua-primary)'
            : 'rgba(1, 157, 220, 0.30)';

        return (
          <div
            key={slot}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              minWidth: 72,
            }}
          >
            <button
              type="button"
              disabled={disabled || busySlot !== null}
              onClick={isInteractive ? () => onSelect!(slot) : undefined}
              aria-pressed={isActive}
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                border: 'none',
                background: chipBg,
                color: '#fff',
                fontWeight: 700,
                fontSize: 17,
                letterSpacing: 0.5,
                cursor:
                  isInteractive && !isBusy ? 'pointer' : 'default',
                boxShadow: isActive
                  ? '0 4px 10px rgba(1, 157, 220, 0.35)'
                  : '0 1px 3px rgba(16, 28, 66, 0.10)',
                transition:
                  'transform 0.1s ease, box-shadow 0.1s ease, background-color 0.15s ease',
                position: 'relative',
              }}
              onMouseDown={(e) => {
                if (isInteractive && !isBusy) {
                  (e.currentTarget as HTMLButtonElement).style.transform =
                    'scale(0.94)';
                }
              }}
              onMouseUp={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'scale(1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'scale(1)';
              }}
            >
              {isBusy ? (
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ width: 18, height: 18, borderWidth: 2 }}
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                slot
              )}
            </button>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: isActive ? 'var(--aqua-text-strong)' : 'var(--aqua-text-muted)',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.1,
                textAlign: 'center',
              }}
            >
              {rpm.toLocaleString()}
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: 'var(--aqua-text-muted)',
                  letterSpacing: 0.5,
                  marginTop: 2,
                }}
              >
                rpm
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
